import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const API_URL = import.meta.env.VITE_API_URL || '';

function RegisterPage() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        confirmPassword: '',
        address1: '',
        city: '',
        postcode: '',
        phonenumber: ''
    });
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });

    // Évaluer la force du mot de passe
    const evaluatePasswordStrength = (password) => {
        let score = 0;
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^a-zA-Z0-9]/.test(password)) score++;

        const strengths = [
            { score: 0, text: 'Très faible', color: 'bg-red-500' },
            { score: 1, text: 'Faible', color: 'bg-orange-500' },
            { score: 2, text: 'Moyen', color: 'bg-yellow-500' },
            { score: 3, text: 'Bon', color: 'bg-blue-500' },
            { score: 4, text: 'Excellent', color: 'bg-green-500' },
            { score: 5, text: 'Très sécurisé', color: 'bg-green-600' }
        ];

        return strengths[score];
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Évaluer la force du mot de passe en temps réel
        if (name === 'password') {
            setPasswordStrength(evaluatePasswordStrength(value));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validations côté client
        if (!formData.firstname || !formData.lastname || !formData.email || !formData.password || 
            !formData.address1 || !formData.city || !formData.postcode || !formData.phonenumber) {
            setError('Tous les champs sont requis');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Les mots de passe ne correspondent pas');
            return;
        }

        if (formData.password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }

        if (!acceptTerms) {
            setError('Vous devez accepter les conditions d\'utilisation');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/whmcs/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstname: formData.firstname,
                    lastname: formData.lastname,
                    email: formData.email,
                    password: formData.password,
                    address1: formData.address1,
                    city: formData.city,
                    postcode: formData.postcode,
                    phonenumber: formData.phonenumber
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || data.details || 'Erreur lors de l\'inscription');
            }

            if (data.success && data.token) {
                // Sauvegarder le token dans localStorage
                localStorage.setItem('authToken', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));

                // Rediriger vers le tableau de bord
                navigate('/admin', { 
                    state: { message: 'Bienvenue ! Votre compte a été créé avec succès.' } 
                });
            } else {
                throw new Error('Réponse invalide du serveur');
            }

        } catch (err) {
            console.error('Erreur inscription:', err);
            setError(err.message || 'Une erreur est survenue lors de l\'inscription');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Inscription - Blog SunServ</title>
                <meta name="description" content="Créez votre compte contributeur sur le blog SunServ et commencez à gagner de l'argent en partageant vos connaissances." />
            </Helmet>

            <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl w-full space-y-8">
                    {/* Logo et titre */}
                    <div className="text-center">
                        <h1 className="text-4xl font-bold text-white mb-2">
                            Sun<span className="text-yellow-400">Serv</span>
                        </h1>
                        <p className="text-gray-400">Créer un compte contributeur</p>
                    </div>

                    {/* Card d'inscription */}
                    <div className="bg-surface rounded-2xl shadow-2xl border border-gray-700/50 p-8">
                        {/* Bannière info */}
                        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                            <p className="text-yellow-400 text-sm flex items-start gap-2">
                                <i className="fas fa-star mt-0.5"></i>
                                <span>
                                    <strong>Gagnez de l'argent !</strong> Recevez <strong>1€</strong> de crédit SunServ pour chaque article publié.
                                </span>
                            </p>
                        </div>

                        {/* Info compte unique */}
                        <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                            <p className="text-blue-400 text-sm flex items-start gap-2">
                                <i className="fas fa-info-circle mt-0.5"></i>
                                <span>
                                    En créant votre compte, vous aurez accès à la fois au <strong>blog contributeur</strong> et à votre <strong>espace client SunServ</strong> avec les mêmes identifiants.
                                </span>
                            </p>
                        </div>

                        <form className="space-y-5" onSubmit={handleSubmit}>
                            {error && (
                                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                                    <p className="text-red-400 text-sm flex items-center gap-2">
                                        <i className="fas fa-exclamation-circle"></i>
                                        {error}
                                    </p>
                                </div>
                            )}

                            {/* Nom et prénom */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="firstname" className="block text-sm font-medium text-gray-300 mb-2">
                                        Prénom *
                                    </label>
                                    <input
                                        id="firstname"
                                        name="firstname"
                                        type="text"
                                        required
                                        value={formData.firstname}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-background border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                                        placeholder="John"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="lastname" className="block text-sm font-medium text-gray-300 mb-2">
                                        Nom *
                                    </label>
                                    <input
                                        id="lastname"
                                        name="lastname"
                                        type="text"
                                        required
                                        value={formData.lastname}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-background border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                                    Email *
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-background border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                                    placeholder="votre@email.com"
                                />
                            </div>

                            {/* Téléphone */}
                            <div>
                                <label htmlFor="phonenumber" className="block text-sm font-medium text-gray-300 mb-2">
                                    Téléphone *
                                </label>
                                <input
                                    id="phonenumber"
                                    name="phonenumber"
                                    type="tel"
                                    required
                                    value={formData.phonenumber}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-background border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                                    placeholder="+33 6 12 34 56 78"
                                />
                            </div>

                            {/* Adresse */}
                            <div>
                                <label htmlFor="address1" className="block text-sm font-medium text-gray-300 mb-2">
                                    Adresse *
                                </label>
                                <input
                                    id="address1"
                                    name="address1"
                                    type="text"
                                    required
                                    value={formData.address1}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-background border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                                    placeholder="123 rue de la République"
                                />
                            </div>

                            {/* Ville et code postal */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="city" className="block text-sm font-medium text-gray-300 mb-2">
                                        Ville *
                                    </label>
                                    <input
                                        id="city"
                                        name="city"
                                        type="text"
                                        required
                                        value={formData.city}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-background border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                                        placeholder="Paris"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="postcode" className="block text-sm font-medium text-gray-300 mb-2">
                                        Code postal *
                                    </label>
                                    <input
                                        id="postcode"
                                        name="postcode"
                                        type="text"
                                        required
                                        value={formData.postcode}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-background border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                                        placeholder="75001"
                                    />
                                </div>
                            </div>

                            {/* Mot de passe */}
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                                    Mot de passe *
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-background border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                                    placeholder="••••••••"
                                />
                                
                                {formData.password && (
                                    <div className="mt-2">
                                        <div className="flex items-center justify-between text-xs mb-1">
                                            <span className="text-gray-400">Force du mot de passe:</span>
                                            <span className={`font-medium ${passwordStrength.score >= 3 ? 'text-green-400' : 'text-orange-400'}`}>
                                                {passwordStrength.text}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-700 rounded-full h-2">
                                            <div 
                                                className={`h-2 rounded-full transition-all ${passwordStrength.color}`}
                                                style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Confirmation mot de passe */}
                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                                    Confirmer le mot de passe *
                                </label>
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-background border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-colors"
                                    placeholder="••••••••"
                                />
                            </div>

                            {/* Conditions */}
                            <div className="flex items-start gap-3">
                                <input
                                    id="terms"
                                    name="terms"
                                    type="checkbox"
                                    checked={acceptTerms}
                                    onChange={(e) => setAcceptTerms(e.target.checked)}
                                    className="mt-1 h-4 w-4 bg-background border-gray-600 rounded text-yellow-400 focus:ring-yellow-400 focus:ring-offset-background"
                                />
                                <label htmlFor="terms" className="text-sm text-gray-300">
                                    J'accepte les{' '}
                                    <a href="https://sunserv.fr/cgu.html" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300">
                                        conditions générales d'utilisation
                                    </a>
                                    {' '}et la{' '}
                                    <a href="https://sunserv.fr/cgu.html#privacy" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300">
                                        politique de confidentialité
                                    </a>
                                </label>
                            </div>

                            {/* Bouton submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 px-4 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Création en cours...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-user-plus"></i>
                                        Créer mon compte
                                    </>
                                )}
                            </button>
                        </form>

                        {/* Footer */}
                        <div className="mt-6 pt-6 border-t border-gray-700">
                            <p className="text-center text-gray-400 text-sm">
                                Vous avez déjà un compte ?{' '}
                                <Link to="/login" className="text-yellow-400 hover:text-yellow-300">
                                    Se connecter
                                </Link>
                            </p>
                            <p className="text-center text-gray-500 text-xs mt-3">
                                <Link to="/" className="hover:text-gray-300 transition-colors">
                                    <i className="fas fa-arrow-left mr-1"></i>
                                    Retour au blog
                                </Link>
                            </p>
                        </div>
                    </div>

                    {/* Avantages */}
                    <div className="bg-surface/50 rounded-xl border border-gray-700/30 p-6">
                        <p className="text-center text-gray-300 text-sm mb-4">
                            <i className="fas fa-gift text-yellow-400 mr-2"></i>
                            <strong>Avantages contributeur</strong>
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-xs text-gray-400">
                            <div>
                                <div className="text-2xl mb-1">💰</div>
                                <div>1€ par article</div>
                            </div>
                            <div>
                                <div className="text-2xl mb-1">✍️</div>
                                <div>Partagez vos savoirs</div>
                            </div>
                            <div>
                                <div className="text-2xl mb-1">🎓</div>
                                <div>Apprenez</div>
                            </div>
                            <div>
                                <div className="text-2xl mb-1">🏆</div>
                                <div>Gagnez en visibilité</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default RegisterPage;
